import { Sparkles, ArrowRight, Sun, User, ImageIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface Props {
  className?: string;
}

const TIPS = [
  { icon: User, label: "Full body, face visible" },
  { icon: Sun, label: "Bright, even lighting" },
  { icon: ImageIcon, label: "Plain background" },
];

/**
 * Glossy animated photo tips card with a shimmering sheen and
 * staggered fade-in chips.
 */
export const PhotoTips = ({ className }: Props) => (
  <div
    className={cn(
      "group relative mt-3 overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-accent/10 p-3 shadow-soft animate-fade-in",
      className,
    )}
  >
    {/* Glossy sheen sweep */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-primary-foreground/20 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-full"
    />
    {/* Continuous shimmer overlay */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,transparent_30%,hsl(var(--primary)/0.08)_50%,transparent_70%)] bg-[length:200%_100%] animate-shimmer"
    />

    <div className="relative">
      <div className="mb-2 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-primary animate-pulse" />
        <p className="text-xs font-semibold text-foreground">Photo tips</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {TIPS.map(({ icon: Icon, label }, i) => (
          <span
            key={label}
            style={{ animationDelay: `${i * 80}ms` }}
            className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-2 py-1 text-[10px] font-medium text-foreground backdrop-blur-sm animate-fade-in transition-transform hover:-translate-y-0.5"
          >
            <Icon className="h-3 w-3 text-primary" />
            {label}
          </span>
        ))}
      </div>

      <Link
        to="/app/photo-tips"
        className="group/link mt-2.5 flex items-center justify-between gap-2 rounded-lg bg-primary px-2.5 py-1.5 text-[11px] font-semibold text-primary-foreground shadow-soft transition-all hover:shadow-md hover:brightness-110"
      >
        <span className="flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" />
          See examples of good vs bad photos
        </span>
        <ArrowRight className="h-3 w-3 shrink-0 transition-transform group-hover/link:translate-x-0.5" />
      </Link>
    </div>
  </div>
);
